// Charts for the Top 10 Jobs overview.
// ----------------------------------------------------------------------------
// Each chart takes the SAME ranked job list from TopJobsOverview, so switching
// the ranking metric re-cuts every panel at once and they always describe the
// same ten jobs. All data comes from topJobsRollup.js — nothing fetches here.
//
// Job labels are long ("9990 - Fab Shop Job Expenses"), so the ranked charts lay
// their bars out horizontally and the axis ticks truncate on one line with the
// full name on hover. Recharts otherwise wraps them onto squeezed lines.

import dayjs from "dayjs";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  ComposedChart,
  Line,
  LineChart,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  LabelList,
} from "recharts";
import { SEVERITY_TIERS } from "./topJobsRollup";

// Distinct hues for the trend lines — the form-type palette would imply these
// lines mean form types.
const TREND_COLORS = ["#155DFC", "#00A63E", "#E17100", "#7C3AED", "#DC2626"];

export const ChartCard = ({ title, subtitle, children, className = "" }) => (
  <div className={`rounded-2xl border border-gray-200 bg-white p-4 shadow-sm ${className}`}>
    <div className="mb-3">
      <h3 className="text-sm font-bold uppercase tracking-wide text-[#0A0A0A]">
        {title}
      </h3>
      {subtitle && <p className="mt-0.5 text-[11px] text-[#717182]">{subtitle}</p>}
    </div>
    {children}
  </div>
);

export const EmptyChart = ({ height = 280, message }) => (
  <div
    style={{ height }}
    className="flex items-center justify-center px-4 text-center text-xs text-gray-400"
  >
    {message}
  </div>
);

// One-line Y-axis job label, truncated with the full name on hover.
export const JobTick = ({ x, y, payload, max = 30 }) => {
  const label = String(payload?.value ?? "");
  const truncated = label.length > max;
  return (
    <text x={x} y={y} dy={3} textAnchor="end" fontSize={10} fill="#4B5563">
      {truncated ? `${label.slice(0, max - 1)}…` : label}
      {truncated && <title>{label}</title>}
    </text>
  );
};

// Rotated X-axis job label for the charts that need jobs along the bottom.
const RotatedJobTick = ({ x, y, payload, max = 20 }) => {
  const label = String(payload?.value ?? "");
  const truncated = label.length > max;
  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={10} textAnchor="end" transform="rotate(-35)" fontSize={9} fill="#4B5563">
        {truncated ? `${label.slice(0, max - 1)}…` : label}
        {truncated && <title>{label}</title>}
      </text>
    </g>
  );
};

// ---------------------------------------------------------------------------
// Safety scorecard — are the busy jobs also the unsafe ones?
// ---------------------------------------------------------------------------
// Failed audit items are shown as a RATE (items per S&H form) rather than a raw
// count, because a raw count just re-ranks by how many audits a job happened to
// receive. The concern-rate line rides a second axis since it is a percentage.
export const JobSafetyScorecard = ({ jobs }) => {
  const data = jobs.filter((j) => j.observations > 0 || j.shpForms > 0);

  if (data.length === 0) {
    return (
      <EmptyChart message="No BBS observations or S&H audits recorded for these jobs in this range." />
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 76, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="displayLabel" interval={0} tick={<RotatedJobTick />} height={10} />
        <YAxis
          yAxisId="left"
          tick={{ fontSize: 10 }}
          label={{
            value: "Failed audits / form",
            angle: -90,
            position: "insideLeft",
            fontSize: 10,
          }}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          unit="%"
          domain={[0, 100]}
          tick={{ fontSize: 10 }}
        />
        <Tooltip
          formatter={(value, name) =>
            name === "BBS Concern Rate" ? [`${value}%`, name] : [value, name]
          }
        />
        <Legend verticalAlign="top" height={26} wrapperStyle={{ fontSize: 11 }} />
        <Bar
          yAxisId="left"
          dataKey="auditFailRate"
          name="Failed audit items per form"
          fill="#00A63E"
          radius={[4, 4, 0, 0]}
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="concernRate"
          name="BBS Concern Rate"
          stroke="#D97706"
          strokeWidth={2}
          dot={{ r: 3 }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

// ---------------------------------------------------------------------------
// Incidents by severity
// ---------------------------------------------------------------------------
export const JobIncidentsChart = ({ jobs }) => {
  // Only jobs that actually had an incident; a row of empty bars says nothing.
  const data = jobs.filter((j) => j.incidents > 0).slice().reverse();

  if (data.length === 0) {
    return <EmptyChart message="No accident or incident reports for these jobs in this range." />;
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 4, right: 40, bottom: 20, left: 8 }}
      >
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
        <YAxis
          type="category"
          dataKey="displayLabel"
          width={190}
          interval={0}
          tick={<JobTick max={26} />}
        />
        <Tooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {SEVERITY_TIERS.map((tier, i) => (
          <Bar
            key={tier.key}
            dataKey={tier.key}
            stackId="sev"
            name={tier.label}
            fill={tier.color}
          >
            {i === SEVERITY_TIERS.length - 1 && (
              <LabelList
                dataKey="incidents"
                position="right"
                style={{ fontSize: 10, fill: "#4B5563" }}
              />
            )}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

// ---------------------------------------------------------------------------
// Activity trend — which jobs are ramping up, which have gone quiet
// ---------------------------------------------------------------------------
export const JobActivityTrend = ({ trend }) => {
  const { data, series } = trend;

  if (data.length < 2) {
    return (
      <EmptyChart message="Not enough history to plot a trend — widen the date range to see month-on-month activity." />
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="month"
          tick={{ fontSize: 10 }}
          tickFormatter={(m) => dayjs(`${m}-01`).format("MMM 'YY")}
        />
        <YAxis
          allowDecimals={false}
          tick={{ fontSize: 10 }}
          label={{ value: "# of forms", angle: -90, position: "insideLeft", fontSize: 10 }}
        />
        <Tooltip labelFormatter={(m) => dayjs(`${m}-01`).format("MMMM YYYY")} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {series.map((job, i) => (
          <Line
            key={job.key}
            type="monotone"
            dataKey={job.key}
            name={job.displayLabel || job.label}
            stroke={TREND_COLORS[i % TREND_COLORS.length]}
            strokeWidth={2}
            dot={{ r: 2 }}
            connectNulls
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};
